import * as constants from 'constant/chat';
import { useContext, useEffect, useRef, useMemo } from 'react';
import { Widget, dropMessages, renderCustomComponent, toggleMsgLoader, addUserMessage } from 'components/ChatWidget';
import isEmpty from 'lodash/isEmpty';
import { Dialog } from 'primereact/dialog';
import BotTitle from './BotTitle';
import { defaultGreetingMessage } from 'constant';
import BotError from 'components/BotError';
import { Answer, BotReply, ChatMessage, IBotDetail, IBotSetting, Intent, SlotsRequired } from 'interface';
import {
    convertFormDataToBotQuery,
    getBotReplyRequest,
    createDynamicFormJson,
    formatStringWithLineBreaks,
    isNoSurveyOrResponseTypeAnswer,
    blurActiveElement,
    updateFilledSlots,
    processFilledSlots,
    preProcessFilledSlots,
    getBotErrorMessage
} from 'utils/chatUtils';
import { Form } from './BotFormControls/Form';
import useBotReply from './hooks/useBotReply';
import DisplayMessageTimeStamp from './ChatbotUtils/DisplayMessageTimeStamp';
import DisplayBotResponseDrafts from './ChatbotUtils/DisplayBotResponseDrafts';
import { DynamicFieldData } from './BotFormControls/dynamic-control-types';
import { ChatbotContext } from 'providers/ChatbotProvider';
import useChat from './hooks/useChat';
import CreateChatMessage from './ChatbotUtils/CreateChatMessage';
import useSessionStorage from 'hooks/useSessionStorage';
import MarkdownComponent from 'components/MarkdownComponent';
import Message from 'components/Message';
import GreetingQuestionItem from './ChatbotUtils/GreetingQuestionItem';
import { MESSAGE_SENDER } from 'components/ChatWidget/constants';
import useLocalStorage from 'hooks/useLocalStorage';

interface ChatbotProps {
    botDetail: IBotDetail;
    resetContext: () => void;
    setBotResponse: (reply: BotReply) => void;
}

const Chatbot = (props: ChatbotProps) => {
    const { botDetail, setBotResponse } = props;
    const { setBotDetail } = useContext(ChatbotContext);

    setBotDetail(botDetail);

    const {
        statesurveyStatus,
        setQuestionId,
        InputDisabledChat,
        InputEnabledChat,
        sessionChatHistory,
        setSessionChatHistory,
        botControllers,
        setBotControllers,
        setSelectedQuickAction,
        setIsGeneralQuerySelected,
        setShowQuickActions,
        newBotSessionLoading,
        expanded,
        setExpanded
    } = useContext(ChatbotContext);

    const { setupChat, cleanupChat, toggleChatLoader, handleReset, isChatEndRef } = useChat({ botDetail });
    const { getSessionStorageValue, replaceSessionStorageValue } = useSessionStorage();
    const { getLocalStorageValue, removeLocalStorageValue } = useLocalStorage();

    const fillerTimeoutRef = useRef<NodeJS.Timeout[]>([]);

    const enableFillers = useMemo(() => {
        return process.env.REACT_APP_ENABLE_FILLERS?.toLowerCase() === 'true';
    }, []);

    const clearFillerTimeouts = () => {
        fillerTimeoutRef.current.forEach((timeout) => clearTimeout(timeout));
        fillerTimeoutRef.current = [];
    };

    const getRandomFiller = (usedMessages: Set<string>): string | null => {
        const availableMessages = constants.FILLER_MESSAGES.filter((msg) => !usedMessages.has(msg));
        if (availableMessages.length === 0) return null;

        const randomIndex = Math.floor(Math.random() * availableMessages.length);
        return availableMessages[randomIndex];
    };

    const addTypingFiller = () => {
        if (!enableFillers) {
            return;
        }

        if (fillerTimeoutRef.current.length > 0) {
            // console.log("Filler already scheduled or displayed. Skipping.");
            return;
        }

        const message = getRandomFiller(new Set());
        if (!message) {
            // console.log("No filler messages available.");
            return;
        }

        // console.log(`Scheduling filler to appear after 3 seconds:`, message);

        const timeout = setTimeout(() => {
            // console.log(`[3.0s] Showing filler:`, message);

            setSessionChatHistory((prev: ChatMessage[]): ChatMessage[] => [
                ...prev,
                CreateChatMessage({
                    type: constants.MESSAGE_TYPE_BOT,
                    component: ({ message }) => <MarkdownComponent>{message}</MarkdownComponent>,
                    componentProps: { message }
                })
            ]);
        }, 3000);

        fillerTimeoutRef.current.push(timeout);
    };

    useEffect(() => {
        setupChat();
        handleReset();

        return () => cleanupChat();
    }, []);

    useEffect(() => updateChat(), [sessionChatHistory]);

    useEffect(() => {
        const wasNavigatedFromBot = getLocalStorageValue('BOT_LINK_NAVIGATION');
        const sessionHistoryMessages = getLocalStorageValue(constants.CHAT_MESSAGES) as any[];

        removeLocalStorageValue('BOT_LINK_NAVIGATION');

        if (!wasNavigatedFromBot || !sessionHistoryMessages) {
            setChatGreetingMessage();
            return;
        }

        const botSessionIdToRetain = getLocalStorageValue(constants.BOT_SESSION_ID);
        if (botSessionIdToRetain) {
            replaceSessionStorageValue(constants.BOT_SESSION_ID, botSessionIdToRetain);

            const verifySessionId = getSessionStorageValue(constants.BOT_SESSION_ID);

            if (verifySessionId === botSessionIdToRetain) {
                removeLocalStorageValue(constants.BOT_SESSION_ID);
            } else {
                console.error('Session ID transfer failed');
            }
        }
        const historyChatMessages = [];

        for (const sessionMessage of sessionHistoryMessages) {
            const { sender, messageJson, createdAt } = sessionMessage;

            const parsedMessageJson = JSON.parse(messageJson);
            if (sender.toLowerCase() === 'agent') {
                historyChatMessages.push({
                    ...CreateChatMessage({
                        type: constants.MESSAGE_TYPE_BOT,
                        component: () => {
                            return <MarkdownComponent>{parsedMessageJson?.answer?.[0]}</MarkdownComponent>;
                        },
                        componentProps: { message: parsedMessageJson?.answer?.[0] }
                    }),
                    timeStamp: new Date(createdAt)
                });
            } else if (sender.toLowerCase() === 'client') {
                const { message } = parsedMessageJson;
                historyChatMessages.push({
                    type: constants.MESSAGE_TYPE_USER,
                    message: message?.text,
                    answerId: '1',
                    timeStamp: new Date(createdAt),
                    drafts: null,
                    component: null
                });
            } else {
                throw new Error('Invalid sender type in session message');
            }
        }

        const greeting = (botDetail?.botSettings?.greetingMessage ?? defaultGreetingMessage) || defaultGreetingMessage;
        setSessionChatHistory([
            ...historyChatMessages,
            CreateChatMessage({
                type: constants.MESSAGE_TYPE_GREETING,
                component: Message,
                timeStamp: null,
                chatQueries: botDetail.botSettings.chatQueries?.map((x) => x.text) ?? [],
                handleChatQueryClick(chatQuery, type, item, answer) {
                    handleUserMessage(chatQuery, {
                        triggerIntent: true,
                        renderMessage: true,
                        sessionId: getSessionStorageValue(constants.BOT_SESSION_ID),
                        surveyStatus: statesurveyStatus,
                        filledSlots: null,
                        botResponse: answer,
                        botControllers: {
                            website: botDetail.websites.length > 0,
                            document: botDetail.documents.length > 0,
                            api: botDetail.apis.length > 0,
                            small_talk: botDetail.botSettings.isSmallTalk,
                            agentFacing: true
                        },
                        type,
                        item
                    });
                },
                componentProps: {
                    message: greeting
                }
            })
        ]);

        removeLocalStorageValue(constants.CHAT_MESSAGES);
    }, []);

    const {
        isIntentFlow: isIntentActionEnabled,
        staticIntentGreetingMessage,
        knowledgeSourceActionLabel
    } = botDetail.botSettings as IBotSetting;

    var showExpanded = expanded;

    const botDetails = botDetail;

    const { getBotReply } = useBotReply();

    const setChatGreetingMessage = () => {
        const greeting = (botDetail?.botSettings?.greetingMessage ?? defaultGreetingMessage) || defaultGreetingMessage;
        setSessionChatHistory([
            CreateChatMessage({
                type: constants.MESSAGE_TYPE_GREETING,
                component: Message,
                timeStamp: null,
                chatQueries: botDetail.botSettings.chatQueries?.map((x) => x.text) ?? [],
                handleChatQueryClick(chatQuery, type, item, answer) {
                    handleUserMessage(chatQuery, {
                        triggerIntent: true,
                        renderMessage: true,
                        sessionId: getSessionStorageValue(constants.BOT_SESSION_ID),
                        surveyStatus: statesurveyStatus,
                        filledSlots: null,
                        botResponse: answer,
                        botControllers: {
                            website: botDetail.websites.length > 0,
                            document: botDetail.documents.length > 0,
                            api: botDetail.apis.length > 0,
                            small_talk: botDetail.botSettings.isSmallTalk,
                            agentFacing: true
                        },
                        type,
                        item
                    });
                },
                componentProps: {
                    message: greeting
                }
            })
        ]);
    };

    // Bot Messages

    const getGreetingMessage = (message: string) => {
        return CreateChatMessage({
            type: constants.MESSAGE_TYPE_BOT,
            component: () => <p>{message}</p>,
            componentProps: {
                message: message
            }
        });
    };

    const getSlotFormMessage = (reply: BotReply, updatedBotControllers) => {
        const botMessage = formatStringWithLineBreaks(reply.answers[0].answer[0] || '');
        const slotsRequired: SlotsRequired[] = reply.slotsRequired;
        const intent: string = reply.intent;

        //! REMOVE THIS MOCK DATA AFTER TESTING
        // const dynamicFormJson: DynamicFieldData[] = createDynamicFormJson(
        //     mockSlotsRequiredJson.slotsRequired as unknown as SlotsRequired[]
        // );

        const dynamicFormJson: DynamicFieldData[] = createDynamicFormJson(slotsRequired);

        const handleSlotFormSubmit = (filledSlots: Record<string, string>) => {
            const displayFilledSlots = updateFilledSlots(filledSlots, slotsRequired);

            const displayFilledSlotsString = Object.entries(displayFilledSlots)
                .map(([key, value]) => `${key}: ${value}`)
                .join('\n\n');

            const preProcessedFilledSlots = preProcessFilledSlots(filledSlots, slotsRequired);

            handleUserMessage(displayFilledSlotsString, {
                sessionId: getSessionStorageValue(constants.BOT_SESSION_ID),
                surveyStatus: statesurveyStatus,
                filledSlots: processFilledSlots(preProcessedFilledSlots, slotsRequired),
                displayMessage: convertFormDataToBotQuery(displayFilledSlots),
                botControllers: updatedBotControllers
            });

            blurActiveElement(); // to remove focus from input field of slot form
        };

        return CreateChatMessage({
            type: constants.MESSAGE_TYPE_BOT,
            component: Form,
            componentProps: {
                message: botMessage,
                fields: dynamicFormJson,
                intent,
                onSubmit: handleSlotFormSubmit
            }
        });
    };

    const handleQuickAction = (action: string) => {
        setSelectedQuickAction(action);

        const isGeneralQueryClicked: boolean =
            action?.toLowerCase() === (knowledgeSourceActionLabel?.toLowerCase() ?? '');

        setIsGeneralQuerySelected(isGeneralQueryClicked);

        const updatedBotControllers = {
            website: isGeneralQueryClicked,
            document: isGeneralQueryClicked,
            api: !isGeneralQueryClicked,
            small_talk: botDetail.botSettings.isSmallTalk,
            agentFacing: true
        };

        setBotControllers((prev) => {
            return { ...prev, ...updatedBotControllers };
        });

        if (isGeneralQueryClicked) {
            InputEnabledChat();
            setSessionChatHistory((prev: ChatMessage[]): ChatMessage[] => {
                return [...prev, getGreetingMessage(staticIntentGreetingMessage)];
            });
            return;
        }

        handleUserMessage(action, {
            triggerIntent: true,
            sessionId: getSessionStorageValue(constants.BOT_SESSION_ID),
            surveyStatus: statesurveyStatus,
            filledSlots: null,
            botControllers: updatedBotControllers
        });
    };

    const renderLogic = (reply: BotReply, chatEnd: boolean) => {
        reply.answers.forEach((ans: Answer) => {
            const { responseType, responseBy } = ans;

            const areSlotsRequired = reply.slotsRequired?.length > 0;
            const showForm = (responseBy !== 'survey' || responseType === 'answer') && areSlotsRequired;
            setShowQuickActions(!showForm && !chatEnd);
            setIsGeneralQuerySelected(false);

            //If intent is received from ML then render slot filling form otherwise render bot response (No survey case)
            if (!isNoSurveyOrResponseTypeAnswer(responseType, responseBy)) {
                DisplayMessageTimeStamp({ messageType: constants.MESSAGE_TYPE_BOT, timeStamp: new Date() });
                InputDisabledChat();
                return;
            }

            if (showForm) {
                InputDisabledChat();
                const updatedBotControllers = {
                    website: false,
                    document: false,
                    api: true,
                    small_talk: botDetail.botSettings.isSmallTalk,
                    agentFacing: true
                };

                setBotControllers((prev) => {
                    return { ...prev, ...updatedBotControllers };
                });

                setSessionChatHistory((prev: ChatMessage[]): ChatMessage[] => {
                    return [...prev, getSlotFormMessage(reply, updatedBotControllers)];
                });
                return;
            }

            setSessionChatHistory((prev: ChatMessage[]): ChatMessage[] => {
                return [
                    ...prev,
                    CreateChatMessage({
                        type: constants.MESSAGE_TYPE_BOT,
                        drafts: reply.answers[0].answer,
                        component: ({ message }) => <MarkdownComponent>{message}</MarkdownComponent>,
                        componentProps: { message: reply.answers[0].answer[0] }
                    }),
                    CreateChatMessage({
                        type: constants.MESSAGE_TYPE_QUICK_BUTTONS,
                        component: Message,
                        timeStamp: null,
                        chatQueries: botDetail.botSettings.chatQueries?.map((x) => x.text) ?? [],
                        handleChatQueryClick(chatQuery, type, item, answer) {
                            handleUserMessage(chatQuery, {
                                triggerIntent: true,
                                renderMessage: true,
                                sessionId: getSessionStorageValue(constants.BOT_SESSION_ID),
                                surveyStatus: statesurveyStatus,
                                filledSlots: null,
                                botResponse: answer,
                                botControllers: {
                                    website: botDetail.websites.length > 0,
                                    document: botDetail.documents.length > 0,
                                    api: botDetail.apis.length > 0,
                                    small_talk: botDetail.botSettings.isSmallTalk,
                                    agentFacing: true
                                },
                                type,
                                item
                            });
                        },
                        componentProps: {
                            message: ''
                        }
                    })
                ];
            });
        });
    };

    const updateChat = () => {
        dropMessages();
        sessionChatHistory.forEach((chatMessage: ChatMessage, chatMessageIndex: number) => {
            const shouldDisableComponent = chatMessageIndex !== sessionChatHistory.length - 1;

            if (chatMessage.type === constants.MESSAGE_TYPE_USER) {
                if (chatMessage.component) {
                    renderCustomComponent(chatMessage.component, {
                        shouldDisableComponent,
                        sender: MESSAGE_SENDER.CLIENT
                    });
                } else {
                    addUserMessage(chatMessage.message);
                    //DisplayBotResponseDrafts({ chatMessage, chatMessageIndex });
                }
                // TimeStamp({
                //     messageType: constants.MESSAGE_TYPE_USER,
                //     timeStamp: chatMessage.timeStamp
                // });
                return;
            }

            if (chatMessage.component) {
                renderCustomComponent(chatMessage.component, { shouldDisableComponent });

                return;
            } else {
                DisplayBotResponseDrafts({ chatMessage, chatMessageIndex });
            }
            if (chatMessage.timeStamp) {
                // TimeStamp({
                //     messageType: constants.MESSAGE_TYPE_BOT,
                //     timeStamp: chatMessage.timeStamp
                // });
            }
        });
    };

    const handleUserMessage = async (
        userMessage: string,
        {
            triggerIntent = false,
            skip = false,
            renderMessage = true,
            sessionId,
            surveyStatus,
            filledSlots = null,
            clientMessageList = null,
            displayMessage = null,
            botControllers,
            type = 'DEFAULT',
            item = null,
            botResponse = null,
            message_type = constants.MESSAGE_TYPE_USER
        }: any
    ) => {
        toggleMsgLoader();
        InputDisabledChat();
        setQuestionId(0); //to disable question on loading also
        DisplayMessageTimeStamp({ messageType: constants.MESSAGE_TYPE_USER, timeStamp: new Date() });

        clearFillerTimeouts();
        addTypingFiller();

        if (renderMessage) {
            setSessionChatHistory((prev: ChatMessage[]): ChatMessage[] => [
                ...prev.filter(
                    (x) => x.type !== constants.MESSAGE_TYPE_GREETING && x.type !== constants.MESSAGE_TYPE_QUICK_BUTTONS
                ),
                {
                    //@ts-ignore
                    type: message_type,
                    message: displayMessage || userMessage,
                    answerId: null,
                    timeStamp: new Date(),
                    drafts: null,
                    component:
                        type === 'CUSTOM'
                            ? () => {
                                  return (
                                      <GreetingQuestionItem
                                          {...{
                                              containerClass: 'active',
                                              icon: item.icon,
                                              query: item.chatQuery,
                                              onClick: () => {}
                                          }}
                                      />
                                  );
                              }
                            : null
                }
            ]);
        }

        const botRequest = getBotReplyRequest({
            intentTriggered: triggerIntent ? userMessage : null,
            botDetails,
            botControllers,
            sessionId,
            userMessage,
            clientMessageList,
            surveyStatus,
            skip,
            filledSlots
        });

        if (type === 'CUSTOM') {
            setTimeout(() => {
                clearFillerTimeouts();
                onResponse({ isSuccess: true, botResponse });
                toggleChatLoader();
            }, 2000);
        } else {
            await getBotReply({
                ...botRequest,
                llmParameters: { ...botRequest.llmParameters, llm: null },
                sessionSlots: []
            })
                .then(({ isSuccess, botResponse }) => {
                    clearFillerTimeouts();
                    onResponse({ isSuccess, botResponse });
                })
                .catch((err) => {
                    clearFillerTimeouts();
                    console.error(err);
                })
                .finally(toggleChatLoader);
        }
    };

    const onResponse = ({ isSuccess, botResponse }) => {
        if (!isSuccess) {
            const isSessionActive = !botResponse.errors.includes('Session expired');

            setSessionChatHistory((prev: ChatMessage[]): ChatMessage[] => {
                const errorMessage = !isSessionActive ? botResponse.errors.join(',') : getBotErrorMessage(botResponse);

                return [
                    ...prev,
                    CreateChatMessage({
                        type: constants.MESSAGE_TYPE_BOT,
                        drafts: null,
                        component: BotError,
                        componentProps: {
                            message: errorMessage,
                            isError: true
                        }
                    })
                ];
            });

            if (!isIntentActionEnabled) {
                InputEnabledChat();
                return;
            }

            if (isSessionActive) {
                setSessionChatHistory((prev: ChatMessage[]): ChatMessage[] => {
                    return [...prev, getGreetingMessage('Would you like to ask anything else?')];
                });
            }

            return;
        }

        const reply: BotReply = botResponse;

        reply.answers.forEach((ans: Answer) => {
            ans.answer = ans.answer.map((botAnswer: string) => {
                return botAnswer.replace(/^mdx/, '');
            });
        });

        // Just for testing
        // reply.answers[0].answer[0] = reply.answers[0].answer[0] + ' [Test Link](http://localhost:3001/)';

        const { answers, sessionId } = reply;
        const { context } = answers[0];
        botResponse.session_id = sessionId;

        if (!reply || isEmpty(reply.answers)) return;

        setBotResponse(reply);

        const { apis } = botDetails as IBotDetail;

        const triggeredIntent: Intent | undefined = apis.find((api: Intent) => {
            const { intentDetail } = api;
            if (!context || context.length === 0) return false;

            const { sourceName } = context[0];
            return intentDetail.name?.toLowerCase() === sourceName?.toLowerCase();
        });

        if (!isEmpty(apis) && !isChatEndRef.current && triggeredIntent) {
            const { isLastIntent } = triggeredIntent.intentDetail;
            isChatEndRef.current = isLastIntent;
        }

        if (isChatEndRef.current) {
            InputDisabledChat();
            setShowQuickActions(false);
        } else {
            InputEnabledChat();
        }

        renderLogic(reply, isChatEndRef.current);
    };

    return (
        <>
            <div className="chat-window-static form-field">
                {!showExpanded && (
                    <Widget
                        handleNewUserMessage={(e) => {
                            handleUserMessage(e, {
                                skip: false,
                                sessionId: getSessionStorageValue(constants.BOT_SESSION_ID),
                                surveyStatus: statesurveyStatus,
                                botControllers
                            });
                        }}
                        // @ts-ignore
                        title={
                            <BotTitle
                                botDetails={botDetails}
                                onClickResetConversation={() => {
                                    handleReset();
                                    setChatGreetingMessage();
                                    props.resetContext();
                                }}
                            />
                        }
                        subtitle=""
                        showTimeStamp={false}
                        fullScreenMode={false}
                        autoFocus={true}
                        disabledInput
                        transcriptionAudioUrl={process.env.REACT_APP_V6_API_URL}
                        launcherCloseImg={'https://storage.googleapis.com/seek-logos/ibexco-assests/close.svg'}
                        launcherOpenImg={'https://storage.googleapis.com/seek-logos/ibexco-assests/chat.svg'}
                        handleQuickButtonClicked={(action) => handleQuickAction(action)}
                        resizable={false}
                        disclaimer={botDetails.botSettings.disclaimer}
                        loading={newBotSessionLoading}
                        tabIndex={0} // Added tabIndex
                    />
                )}
            </div>

            <div className="chat-window-static form-field">
                <Dialog
                    visible={showExpanded}
                    style={{ width: '625px' }}
                    onHide={() => {
                        setExpanded(false);
                    }}
                    draggable={false}
                    closable={false}
                    closeOnEscape={true}
                    showHeader={false}
                    blockScroll={false}
                    resizable={false}
                    modal>
                    <div
                        className="bot-banner"
                        style={{
                            background:
                                botDetails?.botSettings.banner !== ''
                                    ? `url(${botDetails?.botSettings.banner})`
                                    : '#FAFAFA'
                        }}></div>
                    <Widget
                        handleNewUserMessage={(e) => {
                            handleUserMessage(e, {
                                skip: false,
                                sessionId: getSessionStorageValue(constants.BOT_SESSION_ID),
                                surveyStatus: statesurveyStatus,
                                botControllers
                            });
                        }}
                        // @ts-ignore
                        title={
                            <BotTitle
                                botDetails={botDetails}
                                onClickResetConversation={() => {
                                    handleReset();
                                    setChatGreetingMessage();
                                    props.resetContext();
                                }}
                            />
                        }
                        subtitle=""
                        showTimeStamp={false}
                        fullScreenMode={false}
                        autoFocus={true}
                        expanded={showExpanded}
                        disabledInput
                        transcriptionAudioUrl={process.env.REACT_APP_V6_API_URL}
                        handleQuickButtonClicked={(action) => handleQuickAction(action)}
                        disclaimer={botDetails.botSettings.disclaimer}
                        loading={newBotSessionLoading}
                        tabIndex={0} // Added tabIndex
                    />
                </Dialog>
            </div>
        </>
    );
};

export default Chatbot;
